import { useState, useCallback } from 'react'
import type { ModelData, ConfigurationState } from '../types'
import { parseDirectProductCode, applyDirectOptions } from '../utils/directProductCode'

const initialConfig: ConfigurationState = {
  model: null,
  variant: '',
  handleSide: '' as 'left' | 'right' | '',
  sideGlass: '' as 'left' | 'right' | '',
  glass: '',
  ceramic: '',
  handle: '',
  bezel: '',
  fusionBasic: 'none',
  fusionModules: 'none',
  fusionModulesCount: 0,
  fusionCorner: 'none',
  fusionCornerModules: 'none',
  fusionCornerModulesCount: 0,
  akuPlus: 'none',
  nemoThermalProtection: 'standard',
  thermalModules: 'none',
  thermalModulesCount: 1,
  thermalHangers: 'none',
  // Vis-a-vis specific
  'front-variant': '',
  'front-handleSide': '',
  'front-handle': '',
  'front-bezel': '',
  'back-variant': '',
  'back-handleSide': '',
  'back-handle': '',
  'back-bezel': ''
}

export const useConfiguration = () => {
  const [currentModel, setCurrentModel] = useState<ModelData | null>(null)
  const [currentModelGroup, setCurrentModelGroup] = useState<string>('')
  const [config, setConfig] = useState<ConfigurationState>(initialConfig)
  const [showLiftModal, setShowLiftModal] = useState(false)

  const updateConfig = useCallback((key: keyof ConfigurationState, value: any) => {
    setConfig(prev => {
      let newConfig = { ...prev, [key]: value };

      // Auto-set handleSide when LIFT variant is selected for vis-a-vis models
      // LIFT has handle color but no handle side selection
      if (key === 'front-variant' && value === 'lift') {
        newConfig['front-handleSide'] = 'not-applicable';
        // Keep front-handle available for color selection
      }

      if (key === 'back-variant' && value === 'lift') {
        newConfig['back-handleSide'] = 'not-applicable';
        // Keep back-handle available for color selection
      }

      // Clear handle fields when switching from LIFT to standard
      if (key === 'front-variant' && value === 'standard') {
        newConfig['front-handleSide'] = '';
        newConfig['front-handle'] = '';
      }

      if (key === 'back-variant' && value === 'standard') {
        newConfig['back-handleSide'] = '';
        newConfig['back-handle'] = '';
      }

      // --- AUTO-SET HANDLE SIDE FOR STANDARD CORNER MODELS WITH SIDE GLASS ---
      if (key === 'sideGlass' && value && newConfig.variant === 'standard' && currentModel?.glassType === 'narozna') {
        const isDragonOrNemo = currentModel?.name?.toLowerCase().includes('dragon') || currentModel?.name?.toLowerCase().includes('nemo');
        if (isDragonOrNemo) {
          // Right side glass = handle on left, Left side glass = handle on right
          newConfig.handleSide = value === 'right' ? 'left' : 'right';
        }
      }

      // --- AUTO-SELECT GLASS IF ONLY ONE OPTION (for narozne etc.) ---
      // Użyj currentModel zamiast prev.model (prev.model to string/null)
      if (key === 'variant' && currentModel && currentModel.glassType === 'narozna') {
        const glassTypes = new Set<string>();
        if (currentModel.basePrices?.standard) {
          Object.keys(currentModel.basePrices.standard).forEach(glassType => glassTypes.add(glassType));
        }
        if (currentModel.basePrices?.lift) {
          Object.keys(currentModel.basePrices.lift).forEach(glassType => glassTypes.add(glassType));
        }
        if (glassTypes.size === 1) {
          const onlyGlass = Array.from(glassTypes)[0];
          newConfig.glass = onlyGlass;
        }
      }

      return newConfig;
    });
  }, [currentModel])

  // Special function for checking if we should show LIFT modal
  const checkAndHandleLiftSelection = useCallback((key: keyof ConfigurationState, value: any) => {
    // Check if this is a vis-a-vis model with LIFT selected on front
    const isVisAVisWithLift = (
      currentModelGroup === 'dragon-vis-a-vis' || 
      currentModelGroup === 'dragon-vis-a-vis-aku' ||
      currentModelGroup === 'nemo-vis-a-vis'
    );
    
    if (isVisAVisWithLift && key === 'front-variant' && value === 'lift') {
      // Show the LIFT notification modal
      setShowLiftModal(true);
      
      // Also force back variant to standard and disable LIFT for back
      setConfig(prev => ({
        ...prev,
        [key]: value,
        'front-handleSide': 'not-applicable',
        'back-variant': 'standard',
        'back-handleSide': '',
        'back-handle': ''
      }));
    } else {
      // Normal config update
      updateConfig(key, value);
    }
  }, [currentModelGroup, updateConfig])

  const closeLiftModal = useCallback(() => {
    setShowLiftModal(false);
  }, [])

  const setCurrentModelWithGroup = useCallback((model: ModelData | null, groupKey?: string) => {
    setCurrentModel(model);
    if (groupKey) {
      setCurrentModelGroup(groupKey);
    }
  }, [])

  const resetConfig = useCallback(() => {
    const baseConfig = { ...initialConfig };
    setConfig(baseConfig);
  }, [currentModel])

  const resetConfigExceptVariant = useCallback((keepVariant: string) => {
    const baseConfig = {
      ...initialConfig,
      variant: keepVariant
    };
    
    setConfig(baseConfig);
  }, [currentModel])

  const loadConfigFromCode = useCallback((productCode: string) => {
    const parsed = parseDirectProductCode(productCode)
    if (parsed.config) {
      setConfig(prev => {
        const newConfig = applyDirectOptions(prev, parsed.config)
        return newConfig
      })
    }
    return parsed
  }, [])

  return {
    currentModel,
    setCurrentModel: setCurrentModelWithGroup,
    currentModelGroup,
    config,
    updateConfig,
    checkAndHandleLiftSelection,
    showLiftModal,
    closeLiftModal,
    resetConfig,
    resetConfigExceptVariant,
    loadConfigFromCode
  }
}
